import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Briefcase } from "lucide-react";
import {
  listarFuncoesTarefa,
  criarFuncaoTarefa,
  atualizarFuncaoTarefa,
  excluirFuncaoTarefa,
  FuncaoTarefa,
} from "../services/funcoesTarefa";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

// Catálogo de funções DENTRO da tarefa — gestor/admin cria/edita; aparece no
// seletor de pessoas do card ("quem faz o quê"). Molde: pages/Setores.tsx.
// Não confundir com SETOR: o setor é da pessoa (fixo, agrupa a Produtividade);
// a função é do par pessoa+card e pode mudar de tarefa pra tarefa.
export function FuncoesTarefa() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  if (!can(["gestor", "admin"])) return <Navigate to="/" replace />;
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: funcoes = [], isLoading } = useQuery({
    queryKey: ["funcoes-tarefa", "todos"],
    queryFn: () => listarFuncoesTarefa({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: criarFuncaoTarefa,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["funcoes-tarefa"] }); setNovo(false); },
  });
  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarFuncaoTarefa(id, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["funcoes-tarefa"] });
      // Renomear propaga pros cards — recarrega as tarefas pra não mostrar o nome antigo.
      qc.invalidateQueries({ queryKey: ["tarefas"] });
      setEditandoId(null);
    },
    onError: (e: any) => confirm({ title: "Não foi possível salvar", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });
  const excluirM = useMutation({
    mutationFn: excluirFuncaoTarefa,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["funcoes-tarefa"] }),
    onError: (e: any) => confirm({ title: "Não foi possível excluir", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });

  async function aoExcluir(f: FuncaoTarefa) {
    const ok = await confirm({
      title: `Excluir a função "${f.nome}"?`,
      message: "Se algum card já usa essa função, a exclusão é bloqueada — desative-a para sumir do seletor sem apagar o histórico.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(f.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Briefcase size={20} /> Funções na tarefa
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            O que cada pessoa faz dentro de um card (ex.: Edição, Motion, Captação, Roteiro). Ao alocar
            alguém na tarefa, escolhe-se a função dela ali — duas pessoas do mesmo setor podem ter funções diferentes.
          </p>
        </div>
        <button onClick={() => setNovo(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Nova função
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Cor</th>
              <th className="text-left px-4 py-2 font-medium">Função</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && (
              <LinhaEditavel funcao={null} onSalvar={(d) => criarM.mutate(d)} onCancelar={() => setNovo(false)} salvando={criarM.isPending} />
            )}
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
            {!isLoading && funcoes.length === 0 && !novo && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhuma função cadastrada. Crie a primeira.</td></tr>
            )}
            {funcoes.map((f) =>
              editandoId === f.id ? (
                <LinhaEditavel key={f.id} funcao={f} onSalvar={(d) => atualizarM.mutate({ id: f.id, dados: d })} onCancelar={() => setEditandoId(null)} salvando={atualizarM.isPending} />
              ) : (
                <tr key={f.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2"><span className="inline-block w-5 h-5 rounded" style={{ background: f.cor }} /></td>
                  <td className="px-4 py-2 font-medium">{f.nome}</td>
                  <td className="px-4 py-2 tabular-nums">{f.ordem}</td>
                  <td className="px-4 py-2">
                    {f.ativo
                      ? <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativa</span>
                      : <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativa</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => atualizarM.mutate({ id: f.id, dados: { ativo: !f.ativo } })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={f.ativo ? "Desativar" : "Ativar"}>
                      {f.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => setEditandoId(f.id)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Editar">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => aoExcluir(f)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaEditavel({ funcao, onSalvar, onCancelar, salvando }: {
  funcao: FuncaoTarefa | null;
  onSalvar: (dados: { nome: string; cor: string; ordem: number; ativo: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [nome, setNome] = useState(funcao?.nome || "");
  const [cor, setCor] = useState(funcao?.cor || "#64748b");
  const [ordem, setOrdem] = useState(funcao?.ordem ?? 0);
  const [ativo, setAtivo] = useState(funcao?.ativo ?? true);

  function salvar() {
    if (!nome.trim()) return;
    onSalvar({ nome: nome.trim(), cor, ordem: Number(ordem) || 0, ativo });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <input type="color" value={cor} onChange={(e) => setCor(e.target.value)} className="w-8 h-7 rounded cursor-pointer border border-slate-300" title="Cor" />
      </td>
      <td className="px-4 py-2">
        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Edição" className="w-full px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" autoFocus />
      </td>
      <td className="px-4 py-2">
        <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value) || 0)} className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Ativa
        </label>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={salvar} disabled={!nome.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar">
          <Check size={14} />
        </button>
        <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar">
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}
